import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import {
  useMapStateStore, useWindowStore, useMapInteractionStore, useConfigStore,
} from '../../stores';
import { animateMapNavigation } from './GoogleMap/GoogleMapHelpers';
import { isValidExternalLocation } from './MapHelpers';

import ConditionalWrapperDivs from '../HelperComponents/ConditionalWrapperDivs';

import GoogleMap from './GoogleMap/GoogleMap';
import MapLevelsNav from './MapLevelsNav/MapLevelsNav';
import MapControls from './MapControls/MapControls';
import MobileMapControls from './MapControls/MobileMapControls';
import MapCompassRose from './MapCompassRose/MapCompassRose';
import ResidenceFilter from '../ResidenceFilter/ResidenceFilter';
import VectreInfo from '../VectreInfo/VectreInfo';
import MapControlsMarkersToggle from './MapControls/MapControlsMarkersToggle/MapControlsMarkersToggle';
import { TourGuideAi } from '../TourGuideAi';

import './Map.scss';

const Map = ({ reset, initialFocusId }) => {
  const isMountedRef = useRef(true);
  // Track if map is fully initialized to prevent focus-grabbing during init
  const [isMapReady, setIsMapReady] = useState(false);

  // Zustand stores
  const isMobile = useWindowStore((state) => state.isMobile);
  const setHasPendingResidenceFocus = useMapStateStore((state) => state.setHasPendingResidenceFocus);
  const setSvgFocusId = useMapStateStore((state) => state.setSvgFocusId);
  const setPendingAmenityModal = useMapStateStore((state) => state.setPendingAmenityModal);

  // Map interaction store
  const setActiveFilterSelections = useMapInteractionStore((state) => state.setActiveFilterSelections);
  const setIsInsideMap = useMapInteractionStore((state) => state.setIsInsideMap);
  const setFocusLatLng = useMapInteractionStore((state) => state.setFocusLatLng);
  const setFocusXYZ = useMapInteractionStore((state) => state.setFocusXYZ);
  const setExternalLocations = useMapInteractionStore((state) => state.setExternalLocations);
  const setIsExternalFocus = useMapInteractionStore((state) => state.setIsExternalFocus);

  const resetSelections = () => {
    setActiveFilterSelections({});
    // setSvgFocusId(null);
    // Also clear focus coordinates to prevent residue from residence detail maps
    setFocusLatLng(null);
    setFocusXYZ(null);
    setHasPendingResidenceFocus(false);
  };

  const handleAmenitySelected = useCallback((amenityPlace, options = {}) => {
    const { openModal = true } = options;
    setExternalLocations([]);
    setIsExternalFocus(false);
    setSvgFocusId(amenityPlace?.svg_id || null);
    setPendingAmenityModal(openModal && !!amenityPlace?.svg_id);

    // When zoomed out below overlay visibility, the MapOverlay isn't mounted
    // so the svgFocusId → focusXYZ → animation chain won't fire.
    // Animate to community center at enableLevels zoom first; once the overlay
    // becomes visible, MapOverlay picks up the svgFocusId and completes the focus.
    const mapInstance = useMapInteractionStore.getState().map;
    const currentOverlayVisible = useMapInteractionStore.getState().overlayVisible;
    if (mapInstance && amenityPlace?.svg_id && !currentOverlayVisible) {
      const { googleMapsConfig } = useConfigStore.getState().mapConfig || {};
      const { zooms, siteSee, campusCenter } = googleMapsConfig || {};
      const center = siteSee?.center || campusCenter;
      const targetZoom = zooms?.enableLevels;
      if (center && targetZoom) {
        animateMapNavigation(mapInstance, center, targetZoom, {
          onComplete: (finalZoom) => {
            if (finalZoom) useMapStateStore.getState().setActiveZoom(finalZoom);
          },
        });
      }
    }
  }, [setSvgFocusId, setPendingAmenityModal, setExternalLocations, setIsExternalFocus]);

  const handleExternalLocationFound = useCallback((locations) => {
    // Drop locations with missing/invalid coordinates — they produce NaN
    // LatLngs that corrupt the map's fitBounds/setZoom and blank the map.
    const validLocations = (locations || []).filter(isValidExternalLocation);
    if (validLocations.length === 0) return;
    setExternalLocations(validLocations);
    setIsExternalFocus(true);
    setFocusLatLng(
      new window.google.maps.LatLng(Number(validLocations[0].lat), Number(validLocations[0].lng)),
    );
  }, [setExternalLocations, setIsExternalFocus, setFocusLatLng]);

  const handleResetToCommunity = useCallback(() => {
    setExternalLocations([]);
    setIsExternalFocus(false);
    setSvgFocusId(null);

    // Animate to community center at enableLevels zoom.
    const mapInstance = useMapInteractionStore.getState().map;
    const { googleMapsConfig } = useConfigStore.getState().mapConfig || {};
    const { zooms, siteSee, campusCenter } = googleMapsConfig || {};
    const center = siteSee?.center || campusCenter;
    const targetZoom = zooms?.enableLevels;

    if (mapInstance && center && targetZoom) {
      animateMapNavigation(mapInstance, center, targetZoom, {
        onComplete: (finalZoom) => {
          if (finalZoom) useMapStateStore.getState().setActiveZoom(finalZoom);
        },
      });
    }
  }, [setExternalLocations, setIsExternalFocus, setSvgFocusId]);

  // Ensure the map is zoomed in at least to the zoom-zone activation level.
  // Used when an AI response is about the community itself, so zoom zones
  // become visible if the user is currently zoomed out further.
  const handleEnsureZoomZoneZoom = useCallback(() => {
    // Clear any prior external/amenity focus so the community view is clean.
    setExternalLocations([]);
    setIsExternalFocus(false);
    setSvgFocusId(null);

    const mapInstance = useMapInteractionStore.getState().map;
    const { googleMapsConfig } = useConfigStore.getState().mapConfig || {};
    const { zooms, siteSee, campusCenter } = googleMapsConfig || {};
    const targetZoom = zooms?.enableZoomZones;
    if (!mapInstance || !targetZoom) return;

    const currentZoom = useMapStateStore.getState().activeZoom
      ?? mapInstance.getZoom();
    if (currentZoom >= targetZoom) return;

    const center = siteSee?.center || campusCenter;
    if (!center) return;

    animateMapNavigation(mapInstance, center, targetZoom, {
      onComplete: (finalZoom) => {
        if (finalZoom) useMapStateStore.getState().setActiveZoom(finalZoom);
      },
    });
  }, [setExternalLocations, setIsExternalFocus, setSvgFocusId]);

  // Set isInsideMap flag when component mounts
  useEffect(() => {
    setIsInsideMap(true);
    return () => {
      setIsInsideMap(false);
    };
  }, [setIsInsideMap]);

  // Reset the map fully when the reset prop is true (on mount).
  // This clears any state left over from residence detail maps so GoogleMap
  // initializes with its default zoom/center instead of a focused residence.
  useEffect(() => {
    if (reset) {
      resetSelections();
    }
  }, [reset]);

  // Cleanup mounted ref on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Mark map as ready after initialization to prevent focus-grabbing
  // This applies inert to the entire map section (including controls) during init
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsMapReady(true);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <div
        className="map-wrapper"
        // Use inert attribute during initialization to prevent any element
        // (including map controls like MapLevelsNav) from grabbing focus
        // and causing unwanted scroll behavior
        {...(!isMapReady && { inert: '' })}
      >
        <GoogleMap initialFocusId={initialFocusId} />

        {initialFocusId && (
          <div className="map__residenceFilterResetBtnWrapper">
            <button
              type="button"
              onClick={(e) => {
                e.currentTarget.blur();
                resetSelections();
              }}
              className="map__residenceFilterResetBtn"
            >
              Show All Residences
            </button>
          </div>
        )}

        {/* Map Controls */}
        <ConditionalWrapperDivs
          classNamesArray={['mobileMapControls']}
          condition={isMobile}
        >
          {isMobile ? <MobileMapControls /> : <MapControls />}

          <MapLevelsNav />

          <ResidenceFilter
            hideByFieldName="field_display_on_interactive_map"
          />

          {isMobile && (
            <>
              <MapControlsMarkersToggle />
              <VectreInfo
                className="mobileMapControl mobileMapControl--logo"
              />
            </>
          )}

        </ConditionalWrapperDivs>

        <MapCompassRose />

      </div>

      <TourGuideAi
        onAmenitySelected={handleAmenitySelected}
        onExternalLocationFound={handleExternalLocationFound}
        onResetToCommunity={handleResetToCommunity}
        onEnsureZoomZoneZoom={handleEnsureZoomZoneZoom}
      />

    </>
  );
};

export default Map;
